/**
 * Invitation Handler Component
 * Handles post-authentication workspace switching for invitation flows
 */
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { InvitationService } from '@/services/invitation-service'
import { getUserData } from '@/utils/auth-storage'
import { logger } from '@/lib/logger'
import { toast } from 'sonner'

interface InvitationHandlerProps {
  children?: React.ReactNode
}

/**
 * Reports whether the invitation has already been redeemed.
 *
 * A failed accept does not always mean the user is not a member: the first
 * attempt can succeed server-side and lose its response, and a Back navigation
 * replays this URL after a completed join. Both surface as
 * "invitation is no longer valid", so the invitation's own status is what
 * separates them from an invitation that is genuinely unusable.
 */
async function isInvitationAlreadyAccepted(invitationId: string): Promise<boolean> {
  try {
    const details = await InvitationService.getInvitationDetails(invitationId)
    return details.status === 'accepted'
  } catch {
    return false
  }
}

/**
 * Component that handles invitation workspace switching after authentication.
 * After accepting the invitation, reloads the page so the session (now updated
 * with the new workspace) is restored cleanly by SessionProvider.
 */
export function InvitationHandler({ children }: InvitationHandlerProps) {
  const [searchParams] = useSearchParams()
  // Initialize as processing if invitation params are present to prevent flash of children
  const [isProcessing, setIsProcessing] = useState(
    !!searchParams.get('invitationId')
  )

  const processedInvitationRef = useRef<string | null>(null)

  useEffect(() => {
    const handleInvitation = async () => {
      const invitationId = searchParams.get('invitationId')

      if (!invitationId) {
        return
      }

      if (processedInvitationRef.current === invitationId) {
        return
      }

      processedInvitationRef.current = invitationId
      setIsProcessing(true)

      try {
        // Brief delay to let auth state settle (e.g. after OTP verification)
        await new Promise(resolve => setTimeout(resolve, 500))

        // Accept the invitation. The backend updates the session with the new workspace.
        // Retry once if the first attempt fails (token refresh race condition).
        const acceptAndSelectWorkspace = async () => {
          const acceptance = await InvitationService.acceptInvitation(invitationId)
          const user = getUserData()
          if (user?.email && acceptance.selectedWorkspace?.id) {
            localStorage.setItem(
              `currentWorkspaceId_${user.email}`,
              acceptance.selectedWorkspace.id,
            )
          }
        }

        try {
          await acceptAndSelectWorkspace()
        } catch {
          await new Promise(resolve => setTimeout(resolve, 1000))
          await acceptAndSelectWorkspace()
        }

        toast.success('Successfully joined the workspace!')

        // Reload the page so SessionProvider picks up the updated session
        // (which now includes the new workspace). This is the most reliable
        // approach since the session token's workspace data was updated server-side.
        window.location.href = '/app'
      } catch (error) {
        logger.error('Failed to accept invitation', error)

        // The join may already have gone through (see above) — those users are
        // members, so send them into the app instead of the "already used"
        // error page, whose only way out is the homepage.
        if (await isInvitationAlreadyAccepted(invitationId)) {
          window.location.href = '/app'
          return
        }

        toast.error('Failed to join workspace. Please try again.')
        window.location.href = `/invitation/${invitationId}`
      }
    }

    handleInvitation()
  }, [searchParams])

  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground">Setting up your workspace...</p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
